import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { getNonPublicUids } from '../../services/firebase/admin.service';
import logger from '../../utils/logger';

/**
 * Privacy Slice
 *
 * Manages the list of non-public user UIDs for filtering content in feeds.
 * Follows the same pattern as shadowbanSlice.
 *
 * Behavior:
 * - For admins: Content from non-public users is still shown (admins see everything)
 * - For non-admins: Content from non-public users is hidden from feeds
 * - Users always see their own posts regardless of privacy setting
 */

interface PrivacyState {
  nonPublicUids: string[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: PrivacyState = {
  nonPublicUids: [],
  isLoading: false,
  error: null,
  lastFetched: null
};

// Async thunk to fetch non-public UIDs
export const fetchNonPublicUids = createAsyncThunk(
  'privacy/fetchNonPublicUids',
  async () => {
    try {
      const uids = await getNonPublicUids();
      return uids;
    } catch (error) {
      logger.error('Error fetching non-public UIDs:', error);
      // Don't reject - return empty array to avoid breaking the app
      return [];
    }
  }
);

const privacySlice = createSlice({
  name: 'privacy',
  initialState,
  reducers: {
    clearNonPublicUids: (state) => {
      state.nonPublicUids = [];
      state.lastFetched = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNonPublicUids.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNonPublicUids.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nonPublicUids = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchNonPublicUids.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch non-public users';
        // Keep existing list on error to avoid breaking feed
      });
  }
});

export const { clearNonPublicUids } = privacySlice.actions;

export default privacySlice.reducer;

// Selectors
export const selectNonPublicUids = (state: { privacy: PrivacyState }) =>
  state.privacy.nonPublicUids;

export const selectPrivacyLastFetched = (state: { privacy: PrivacyState }) =>
  state.privacy.lastFetched;

// Memoized selector that converts array to Set for O(1) lookups
// Use this in feed filtering for better performance
export const selectNonPublicUidsSet = createSelector(
  [selectNonPublicUids],
  (uids): Set<string> => new Set(uids)
);
